import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import moment from 'moment'
import { Box } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { ITrailerShipmentDetails } from 'types/FMSGraphqlTypes'
import * as Apis from 'apps/fleet-planner/utils/api'
import { Spinner, TopNav, Toast, useToastState } from 'transfix-ui'
import { FORMATS } from 'constants/datetime'
import {
  getShipmentState,
  getLatestEvent,
  getLoadLocation,
  getTrailerType,
} from '../../utils/trailers'
import { statusTagMap } from '../equipment-listing/schema/TrailerSchema'
import CurrentLoadCard from './components/CurrentLoadCard'
import TrailerAssignmentCard from './components/TrailerAssignmentCard'
import TrailerDetailsCard from './components/TrailerDetailsCard'
import { useEditTrailer } from './hooks/useEditTrailer'
import { useTrailer } from './hooks/useTrailer'
import { IAssignmentSchema } from './schema/AssignmentSchema'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 72,
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      paddingTop: 0,
    },
  },
  detailsCard: {
    width: '100%',
    padding: '16px 24px',
    [theme.breakpoints.up('md')]: {
      width: '55%',
    },
  },
  assignmentCard: {
    width: '100%',
    padding: '0px 24px 16px 24px',
    [theme.breakpoints.up('md')]: {
      padding: '16px 24px 16px 0px',
      width: '45%',
    },
  },
}))

const TrailerDetailPage = () => {
  const [loadId, setLoadId] = useState<string | null>(null)
  const classes = useStyles()
  const { id, motorCarrierId } = useParams<{ id: string; motorCarrierId: string }>()
  const history = useHistory()
  const { error, loading, trailerDetails } = useTrailer(id)
  const { editTrailer } = useEditTrailer(id)
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const {
    arrivedAt,
    carrierName,
    facility,
    length,
    note,
    number,
    shipmentDetails,
    state,
    trailerType,
  } = trailerDetails || {}
  const {
    deliveryTimezone,
    deliveryTrailerArrivedAt,
    deliveryTrailerEmptiedAt,
    pickupTimezone,
    pickupTrailerArrivedAt,
    pickupTrailerEmptiedAt,
  } = shipmentDetails || {}

  const isDelivery = Boolean(deliveryTrailerArrivedAt && deliveryTrailerEmptiedAt)
  const useFacilityTimezone = !(pickupTrailerArrivedAt && pickupTrailerEmptiedAt) && !isDelivery
  const pickupDeliveryTimezone = isDelivery ? deliveryTimezone : pickupTimezone
  const timezone = useFacilityTimezone ? facility?.timezone : pickupDeliveryTimezone
  const trailerArrivedAt = isDelivery
    ? deliveryTrailerArrivedAt
    : pickupTrailerArrivedAt || arrivedAt
  const trailerEmptiedAt = isDelivery
    ? deliveryTrailerEmptiedAt
    : pickupTrailerEmptiedAt || arrivedAt
  const emptyTrailerArrivalTime = trailerArrivedAt
    ? moment(trailerArrivedAt)
        .tz(timezone || FORMATS.defaultTimeZone)
        .format(FORMATS.militaryTimeFormat)
    : undefined
  const emptyTrailerReadyTime = trailerEmptiedAt
    ? moment(trailerEmptiedAt)
        .tz(timezone || FORMATS.defaultTimeZone)
        .format(FORMATS.militaryTimeFormat)
    : undefined

  const handleBackClick = () => {
    history.push('/equipment')
  }

  // Fetch FP's load id for a given external load id (returns undefined if not existing)
  const getExternalShipmentFleetPlannerId = useCallback(async (): Promise<void> => {
    try {
      const res = await Apis.fetchExternalLoadFleetPlannerId(shipmentDetails?.id || '')

      setLoadId(res.shipmentId)
    } catch {
      setLoadId(null)
    }
  }, [shipmentDetails?.id])

  useEffect(() => {
    getExternalShipmentFleetPlannerId()
  }, [getExternalShipmentFleetPlannerId])

  const showCurrentLoadCard = Boolean(shipmentDetails)

  const handleEditTrailer = async (formParams: IAssignmentSchema) => {
    const response = await editTrailer(formParams)
    if (response.errors) {
      openToast({
        toastType: 'alert',
        toastMessage: 'There was an error updating trailer',
      })
    } else {
      openToast({
        toastType: 'default',
        toastMessage: 'Trailer updated successfully',
      })
    }
  }

  if (loading) {
    return <Spinner position='relative' />
  }

  if (error) {
    return (
      <>
        <TopNav title='Trailer not found' showBackButton onPressBackButton={handleBackClick} />
        <Box display='flex' flexDirection='column' alignItems='center' marginTop={3}>
          <h2>Trailer not found!</h2>
          <p>We couldn&apos;t find the trailer you&apos;re looking for.</p>
        </Box>
      </>
    )
  }

  return (
    <>
      <TopNav title={`Trailer #${number}`} showBackButton onPressBackButton={handleBackClick} />
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      <Box className={classes.container}>
        <Box className={classes.detailsCard}>
          <TrailerDetailsCard
            carrier={carrierName}
            equipmentType={trailerType || '—'}
            size={length || '—'}
          />
          {showCurrentLoadCard && (
            <CurrentLoadCard
              currentDatetime={getLatestEvent(
                shipmentDetails as ITrailerShipmentDetails,
                arrivedAt
              )}
              deliveryLoadLocation={getLoadLocation(
                shipmentDetails?.deliveryCity,
                shipmentDetails?.deliveryStateCode
              )}
              deliveryTrailerType={getTrailerType(shipmentDetails?.deliveryIsDrop)}
              locationDetails={getShipmentState(shipmentDetails?.state)}
              loadId={loadId || ''}
              shipmentId={shipmentDetails?.id}
              pickupLoadLocation={getLoadLocation(
                shipmentDetails?.pickupCity,
                shipmentDetails?.pickupStateCode
              )}
              pickupTrailerType={getTrailerType(shipmentDetails?.pickupIsDrop)}
            />
          )}
        </Box>
        <Box className={classes.assignmentCard}>
          <TrailerAssignmentCard
            activeFacility={facility ? { id: facility.uuid, text: facility.name || '' } : undefined}
            emptyTrailerArrivalDate={trailerArrivedAt || undefined}
            emptyTrailerArrivalTime={emptyTrailerArrivalTime || undefined}
            emptyTrailerReadyDate={trailerEmptiedAt || undefined}
            emptyTrailerReadyTime={emptyTrailerReadyTime || undefined}
            motorCarrierId={motorCarrierId}
            onSubmit={handleEditTrailer}
            note={note || ''}
            shipmentStatus={shipmentDetails?.state}
            trailerStatus={state ? statusTagMap[state] : undefined}
          />
        </Box>
      </Box>
    </>
  )
}

export default TrailerDetailPage
